import React, { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { isMobile } from 'react-device-detect';
import { useNavigate } from "react-router-dom";
import PlayerCenterButtonPlay from "./CenterButton";
import moment from "moment";
import { usePlayerContext } from "../../context/PlayerContext";
import {
    getStreamMeta,
    getStreamSessions,
    getStreamQualities,
    getStreamLanguages,
    getCurrentEpisodeSessions,
    getCurrentDaySessions
} from "../../../../common/src/stream";
import { isEpisodeRecorded } from "../../utils/stream";
import { useScreenOrientation } from "../../hooks/useScreenOrientation";
import { useLocaleContext } from "../../context/LocaleContext";
import { useEventContext } from "../../context/EventContext";
import { CUT_STATUSES, FULLSCREEN_MODES } from "../../constants/common";
import { useStreamContext } from "../../context/StreamContext";
import ButtonClosePlayer from "../../blocks/PlayerBannerBlock/ButtonClosePlayer";
import FloatingPlayerOverlay from "./Overlay/FloatingPlayerOverlay";
import Buffering from "./Controls/Buffering";
import Polls from "../../components/Polls"
import { fullscreenClose, fullscreenOpen, fullscreenSupport, isFullscreen } from "./FullScreen";
import Notifications from "../Notifications";
import Controls from "./Controls";
import Reactions from "./Reactions";
import {
    CHANGE_STREAM_SOURCE_TIMEOUT,
    FIX_STREAM_SOURCE_TIMEOUT,
    RESET_STREAM_SOURCE_TIMEOUT,
    LIVE_DELAY, LIVE_OFFSET,
    LIVE_DETECT_OFFSET,
    STORAGE_KEYS
} from "./consts";
import { getStreamUrls, getQuality, getLanguage, calculateEpisodeDuration, getDurationSession } from "./utils";
import "./style.scss";

export default ({ isHallsPage }) => {
    const CONTROLS_VISIBLE_TIMEOUT = 5000;
    const [stream, setStream] = useState(null);
    const [streamDay, setStreamDay] = useState(undefined);
    const [streamEpisodes, setStreamEpisodes] = useState([]);
    const [episode, setEpisode] = useState(null);
    const [quality, setQuality] = useState(null);
    const [language, setLanguage] = useState(null);
    const [streamStartPosition, setStreamStartPosition] = useState(null);
    const [streamSource, setStreamSource] = useState(null);
    const [streamSources, setStreamSources] = useState(null);
    const [streamSourceIndex, setStreamSourceIndex] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [durationPlayer, setDurationPlayer] = useState(0);
    const [durationSession, setDurationSession] = useState(0);
    // const [isLive, setLive] = useState(false);
    const [isWaiting, setWaiting] = useState(false);
    const [nowTime, setNowTime] = useState(Date.now());
    const [reactionsActive, setReactionsActive] = useState(true);
    const nowTimeTimerRef = useRef(null);
    const playerContainerRef = useRef(null);
    const playerRef = useRef(null);
    const hlsRef = useRef(null);
    const levelRef = useRef(-1);
    const controlsVisibleTimerRef = useRef(null);
    const controlsVisibleRef = useRef(false);
    const livePlayingTimerRef = useRef(null);
    const { name: localeName } = useLocaleContext();
    const { watchKey } = useEventContext();
    const {
        event,
        streamPlayable,
        playingOptions,
        onCurrentTimeChange,
        onDurationChange,
        onPlaying,
        setStream: setPlayingStream,
        changedBroadcastSession,
        isFloating,
        mountPlayer,
        fullScreen,
        setFullScreen,
        tabletView,
        mobileView,
        pageWasPlayerUnpinned,
        paused,
        setPaused,
        setIsShowPlayerHalls,
        containerRef,
        embedEpisode,
        embedEpisodeOffset = 0,
    } = usePlayerContext();
    const { recordStatus } = useStreamContext();
    const navigate = useNavigate();
    const { isOrientationLandscape } = useScreenOrientation();

    const { isLive: isStreamLive } = useMemo(() => getStreamMeta(stream), [stream]);

    const streamQualities = getStreamQualities(stream);
    const streamLanguages = useMemo(() => getStreamLanguages(stream), [stream]);

    const isEmbedEpisodeArchiveStatus = useMemo(() => {
        return embedEpisode && CUT_STATUSES.includes(embedEpisode.archiveStatus);
    }, []);

    const isLiveEmbedEpisode = useMemo(() =>
        embedEpisode && moment().unix() >= embedEpisode.timeStart && moment().unix() <= embedEpisode.timeStop,
        [embedEpisode, nowTime]);

    const duration = useMemo(() => {
        if (embedEpisode && !isEmbedEpisodeArchiveStatus) {
            return Math.min(embedEpisode.timeStop, Math.floor(nowTime / 1000)) - embedEpisode.timeStart;
        } else {
            return durationPlayer !== Infinity ? durationPlayer : durationSession;
        }
    }, [durationPlayer, durationSession, embedEpisode, nowTime]);

    const isLive = isStreamLive && (!embedEpisode || isLiveEmbedEpisode) && currentTime >= (duration - LIVE_DETECT_OFFSET);

    const currentDaySessions = useMemo(() => getCurrentDaySessions(stream, streamDay), [stream, streamDay]);

    const changeDeliveryNetwork = () => {
        if (streamSources?.length > streamSourceIndex + 1) {
            setTimeout(() => setStreamSourceIndex(streamSourceIndex + 1), CHANGE_STREAM_SOURCE_TIMEOUT);
        } else {
            setTimeout(() => setStreamSourceIndex(0), RESET_STREAM_SOURCE_TIMEOUT);
        }
    }

    const updateQuality = (quality) => {
        setQuality(quality)
        levelRef.current = (quality?.number || 0) - 1;
    }

    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (data?.action === 'getInit') {
                    window.parent.postMessage(JSON.stringify({ isPlayerInit: !!playerRef.current }), '*');
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, []);

    useEffect(() => {
        const video = playerRef.current;
        const handlePlay = () => {
            try {
                video.play()
            } catch (e) {
                console.info(e)
            }
        }

        if (!video || !streamSource) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            return;
        }

        const { isLive, isFlussonic } = getStreamMeta(stream);

        const prevPlaying = !video.paused;
        let recoverMediaErrorAttempts = 0;
        let restartLoadingAttempts = 0;
        let startPosition = 0;

        if (embedEpisode && !isLive && !isEmbedEpisodeArchiveStatus) {
            startPosition = embedEpisodeOffset;
        } else if (null !== streamStartPosition) {
            startPosition = streamStartPosition;
            setStreamStartPosition(null);
        } else if (video.currentTime > 0) {
            startPosition = video.currentTime;
        } else if (isLive && !embedEpisode) {
            startPosition = -1;
        } else {
            startPosition = 0;
        }
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        const volume = parseFloat(localStorage.getItem(STORAGE_KEYS.VOLUME));
        if (!isNaN(volume)) {
            video.volume = volume;
        }
        const muted = localStorage.getItem(STORAGE_KEYS.MUTED);
        if (null !== muted) {
            video.muted = parseInt(muted) > 0;
        }

        if (Hls.isSupported()) {
            let hlsConfig = {
                debug: !!localStorage.getItem('hls.debug'),
                maxBufferLength: 10,
                maxMaxBufferLength: 30,
                startPosition,
                startLevel: isFlussonic ? (quality?.number || 0) - 1 : undefined,
            };
            console.log('hlsConfig', hlsConfig);

            hlsRef.current = new Hls(hlsConfig);
            hlsRef.current.loadSource(streamSource);
            hlsRef.current.attachMedia(video);
            hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
                console.debug('hlsError', data, event);
                if (!data.fatal) {
                    return;
                }

                switch (data.type) {
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        if (recoverMediaErrorAttempts++ < 3) {
                            console.warn('hls: try to recoverMediaError');
                            setTimeout(() => hlsRef.current.recoverMediaError(), FIX_STREAM_SOURCE_TIMEOUT);
                            return;
                        }
                        break;
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        if (restartLoadingAttempts++ < 3 && data.details !== Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
                            console.warn('hls: try to startLoad');
                            setTimeout(() => hlsRef.current.startLoad(), FIX_STREAM_SOURCE_TIMEOUT);
                            return;
                        }
                        break;
                }

                console.warn('hls: changeDeliveryNetwork on error: ' + data.type);
                changeDeliveryNetwork();
            });
            hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
                if (prevPlaying) {
                    handlePlay();
                }
            });
            // if (embedEpisode && !isEmbedEpisodeArchiveStatus) {
            //     hlsRef.current.on(Hls.Events.FRAG_CHANGED, (event, data) => {
            //         const fragStart = data.frag.start;
            //         const absoluteTime = embedEpisode.timeStart + fragStart;

            //         if (absoluteTime >= embedEpisode.timeStop) {
            //             playerRef.current.pause();
            //             playerRef.current.currentTime = embedEpisode.timeStop - embedEpisode.timeStart;
            //         }
            //     });
            // }
            if (isFlussonic) {
                hlsRef.current.on(Hls.Events.LEVEL_SWITCHED, () => {
                    const currentLevel = hlsRef.current.currentLevel;
                    if ([currentLevel, levelRef.current].every(level => level > -1) && currentLevel !== levelRef.current) {
                        hlsRef.current.currentLevel = levelRef.current;
                    }
                });
                hlsRef.current.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
                    if (language && hlsRef.current?.audioTracks?.length > 1) {
                        hlsRef.current.audioTrack = ['left', 'right'].indexOf(language.channel);
                    }
                });
            }
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = streamSource;

            if (startPosition >= 0) {
                video.currentTime = startPosition + embedEpisodeOffset;
            } else {
                video.currentTime = (Number.isFinite(video.duration) ? video.duration + embedEpisodeOffset : getDurationSession(stream, streamDay, embedEpisode)) - LIVE_OFFSET;
            }

            const startPositionHandler = (e) => {
                video.removeEventListener(e.type, startPositionHandler);
                if (startPosition >= 0) {
                    video.currentTime = startPosition + embedEpisodeOffset;
                }
            };

            video.addEventListener('canplaythrough', startPositionHandler);

            if (prevPlaying) {
                handlePlay();
            }
        }
    }, [streamSource]);

    // const checkIsLive = useCallback((currentTime, duration) => {
    //     setLive(isStreamLive && currentTime >= (duration - LIVE_DETECT_OFFSET));
    // }, [isStreamLive, LIVE_DETECT_OFFSET]);

    useEffect(() => {
        if (streamSources) {
            setStreamSource(streamSources[streamSourceIndex]);
        } else {
            setStreamSource(null);
        }
    }, [streamSources, streamSourceIndex]);

    useEffect(() => {
        const { isRecord, isWaiting } = getStreamMeta(stream);

        if ((!stream || !streamPlayable || !quality || (streamLanguages?.length > 1 && !language) || (isWaiting && !isRecord))
        ) {
            setStreamSources(null);
            setStreamSourceIndex(0);
        } else {
            const streamUrls = getStreamUrls({ stream, streamDay, quality, language, episode, recordStatus, embedEpisode });
            setStreamSources(streamUrls);
            setStreamSourceIndex(0);
            onPlaying(stream, episode);
        }
    }, [stream, streamDay, quality, language, episode, streamLanguages, embedEpisode, streamPlayable])

    useEffect(() => {
        if (quality && hlsRef.current?.levels?.length > 1) {
            const level = hlsRef.current.currentLevel = quality.number - 1;
            console.log('setLevel: ', level);
        }
    }, [quality]);

    useEffect(() => {
        if (language && hlsRef.current?.audioTracks?.length > 1) {
            const track = hlsRef.current.audioTrack = ['left', 'right'].indexOf(language.channel);
            console.log('audioTrack: ', track);
        }
    }, [language]);

    useEffect(() => {
        if (null === playingOptions) {
            return;
        }

        let isStreamChanged = false;
        let isDayChanged = false;
        let startPosition = playingOptions.startPosition;

        if (playingOptions.stream) {

            setStreamEpisodes(playingOptions.stream.episode || []); // изменилось наполнение зала, обновляем эпизоды

            if (playingOptions.streamDay !== streamDay) {
                setStreamDay(playingOptions.streamDay);
                isDayChanged = true;
            }

            if (playingOptions.stream?.id !== stream?.id) { // изменился зал, обновляем плеер
                isStreamChanged = true;
                setStream(playingOptions.stream);
                updateQuality(getQuality(playingOptions.stream))
                // setQuality(getQuality(playingOptions.stream));

                setLanguage(getLanguage(playingOptions.stream, localeName));

                handleStop(); // тормозим видео
            } else if (playingOptions.stream.status !== stream?.status) { // Изменился статус
                isStreamChanged = true;
                setStream(playingOptions.stream);
            }
        }
        if (playingOptions.episode) { // если выбран нарезанный эпизод, устанавливаем его в качестве источника
            if (playingOptions.episode.id !== episode?.id) {
                isStreamChanged = isEpisodeRecorded(playingOptions.episode) ? true : isStreamChanged;
                setEpisode(playingOptions.episode);
                startPosition = 0;
            } else {
                setTimeout(() => {
                    try {
                        playerRef.current?.play()
                    } catch (e) {
                        console.info(e)
                    }
                }, 100);
            }
        } else {
            setEpisode(null);
        }

        if (startPosition !== undefined) { // произошла навигация по видео
            if (isStreamChanged || isDayChanged || !playerRef.current) {
                setStreamStartPosition(startPosition);
            } else {
                handleSeek(startPosition);
            }
        } else if (isStreamChanged && playingOptions.stream && !embedEpisode) {
            const { isLive } = getStreamMeta(playingOptions.stream);
            setStreamStartPosition(isLive ? -1 : 0);
        }
    }, [playingOptions]);

    useEffect(() => {
        onCurrentTimeChange(currentTime);
        // checkIsLive(currentTime, duration);
    }, [currentTime]);
    useEffect(() => {
        onDurationChange(duration);
        // checkIsLive(currentTime, duration);
    }, [durationPlayer, durationSession]);
    // useEffect(() => {
    //     checkIsLive(currentTime, duration);
    // }, [paused]);

    useEffect(() => {
        let duration = 0;
        if (getStreamSessions(stream).length > 0 && nowTime > 0) {
            duration = isLive && embedEpisode
                ? calculateEpisodeDuration({ episode: embedEpisode, sessions: currentDaySessions })
                : currentDaySessions.reduce((total, { timeStart, timeStop }) => {
                    return total + (timeStop || Math.round(nowTime / 1000)) - timeStart;
                }, 0);
        }

        setDurationSession(duration);
    }, [stream, nowTime, currentDaySessions, embedEpisode]);

    // useEffect(() => {
    //     if (!hlsRef.current || !embedEpisode || !isLive) return;

    //     const handleFragChanged = (event, data) => {
    //         const fragAbsoluteTime = embedEpisode.timeStart + data.frag.start;
    //         if (fragAbsoluteTime >= embedEpisode.timeStop) {
    //             playerRef.current.pause();
    //             playerRef.current.currentTime = embedEpisode.timeStop - embedEpisode.timeStart;
    //         }
    //     };

    //     hlsRef.current.on(Hls.Events.FRAG_CHANGED, handleFragChanged);

    //     return () => {
    //         hlsRef.current.off(Hls.Events.FRAG_CHANGED, handleFragChanged);
    //     };
    // }, [embedEpisode, isLive]);

    useEffect(() => {
        nowTimeTimerRef.current = setInterval(() => setNowTime(Date.now()), 1000);

        return () => {
            clearInterval(nowTimeTimerRef.current);
        }
    }, []);
    useEffect(() => {
        if (!changedBroadcastSession || !stream || stream.id !== changedBroadcastSession.streamId) {
            return;
        }
        clearTimeout(livePlayingTimerRef.current);
        if (!changedBroadcastSession.timeStop) {
            livePlayingTimerRef.current = setTimeout(() => {
                if (streamSources?.length) {
                    setStreamSource(streamSources[streamSourceIndex]);
                }
            }, LIVE_DELAY * 1000);
        }
        // checkIsLive(currentTime, duration);
    }, [changedBroadcastSession]);

    // Предотвращение стандартного поведения видео плеера (прокрутки) при нажатии клавиш влево/вправо

    useEffect(() => {
        if (!stream || stream.dvr) {
            return;
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [stream]);

    const handleKeyDown = (e) => {
        if (e.keyCode === 37 || e.keyCode === 39) {
            e.stopPropagation();
            e.preventDefault();
            return false;
        }
    };

    // const handleSeeked = (currentTime, duration) => checkIsLive(currentTime, duration);
    const handleSeek = (time) => {
        const video = playerRef.current;
        const handlePlay = () => {
            try {
                video.play()
            } catch (e) {
                console.info(e)
            }
        }

        if (!video) return;

        let updatedTime = time;

        if (embedEpisode && !isEmbedEpisodeArchiveStatus) {
            updatedTime = duration;
        } else {
            if (updatedTime === -1) {
                if (isLive) return;
                video.playbackRate = 1;
                const currentDay = stream.stream?.length - 1;
                if (currentDay !== -1 && currentDay !== streamDay) {
                    setPlayingStream(stream);
                    updatedTime = 0;
                } else {
                    updatedTime = stream.dvr ? duration - LIVE_OFFSET : 0;
                }
            }
        }

        if (video.paused && !hlsRef.current) {
            handlePlay();
            const listener = (e) => {
                video.removeEventListener(e.type, listener);
                video.currentTime = updatedTime + embedEpisodeOffset;
            };
            video.addEventListener('canplaythrough', listener);
        } else {
            video.currentTime = updatedTime + embedEpisodeOffset;
        }
        
        setTimeout(() => video.play(), 100);
    }
    const handleQualityChange = (quality) => {
        updateQuality(quality);
        // setQuality(quality);
        localStorage.setItem(STORAGE_KEYS.QUALITY, quality?.number);
    }
    const handleLanguageChange = (language) => {
        setLanguage(language);
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, language?.label);
    }
    const handleVolumeChange = (volume, muted) => {
        localStorage.setItem(STORAGE_KEYS.VOLUME, volume);
        localStorage.setItem(STORAGE_KEYS.MUTED, muted ? '1' : '0');
    }
    const handleClick = (e) => {
        if ((e.target !== e.currentTarget) || isMobile) {
            return;
        }
        if (playerRef.current.paused) {
            try {
                playerRef.current.play();
            } catch (e) {
                console.info(e)
            }
        } else {
            playerRef.current.pause();
        }
    }
    const handleCanPlayThrough = () => { }
    const handleWaiting = () => {
        setWaiting(true);

        const sessions = embedEpisode ? getCurrentEpisodeSessions({ episode: embedEpisode, stream }) : getStreamSessions(stream);

        const lastBroadcastSession = sessions[sessions.length - 1] || null;
        if (null === lastBroadcastSession) {
            return;
        }

        if (lastBroadcastSession.timeStop > 0 && duration - LIVE_OFFSET < currentTime) {
            handleStop();
        }
    }

    const handlePlay = () => {
        setPaused(false);
    }
    const handlePause = () => {
        setPaused(true);
    }
    const handleStop = () => {
        if (playerRef?.current && !playerRef.current.paused) {
            playerRef.current.pause();
            playerRef.current.currentTime = 0 + embedEpisodeOffset;
            setPaused(true);
        }
    }

    const handleShowControls = () => {
        if (controlsVisibleTimerRef.current) {
            clearTimeout(controlsVisibleTimerRef.current);
        }
        controlsVisibleTimerRef.current = setTimeout(() => {
            controlsVisibleRef.current = false;
        }, (CONTROLS_VISIBLE_TIMEOUT / (isFloating ? 3 : 1)));
        controlsVisibleRef.current = true;
    }
    const handleMouseLeave = () => {
        controlsVisibleRef.current = false;
        clearTimeout(controlsVisibleTimerRef.current);
    }

    const handleToggleReactions = () => {
        setReactionsActive(prev => !prev);
    }

    const toggleFullScreen = (mode) => {
        if (fullscreenSupport(containerRef.current)) {
            if (!fullScreen || fullScreen !== mode) {
                setFullScreen(mode);
                fullscreenOpen(containerRef.current);
            } else {
                setFullScreen(null);
                fullscreenClose();
            }
        } else if (fullscreenSupport(playerRef.current)) {
            if (isFullscreen()) {
                fullscreenClose();
            } else {
                fullscreenOpen(playerRef.current);
            }
        }
    }

    const handleNavigate = (time) => {
        if (time && playerRef.current) {
            playerRef.current.currentTime = time + embedEpisodeOffset
        }
    }

    const handleTimeUpdate = (e) => {
        const video = e.target;
        let time = Math.max(0, video.currentTime - embedEpisodeOffset);

        if (embedEpisode && !isEmbedEpisodeArchiveStatus && time >= duration) {
            handleStop()
        }

        setCurrentTime(time);
    };

    const returnFromPIP = (e) => {
        mountPlayer();
        navigate(`/event/${watchKey}${pageWasPlayerUnpinned.current ? '/' + pageWasPlayerUnpinned.current : ''}`)
    }

    if (!streamSources) {
        return null;
    }

    const skipRenderControlsDvrDisabled = !stream.dvr && !isStreamLive;
    const controlsHidden = !(controlsVisibleRef.current || paused);
    const isShowReactions = isStreamLive && event?.moduleMain?.showReaction && (!mobileView || isOrientationLandscape);
    const className = `${fullScreen ? '-landscape' : ''}` + (controlsHidden ? ' stream-player-controls-hidden' : '') + (isFloating ? ' is-floating' : '');

    return (
        <div
            id="stream-player-container"
            ref={playerContainerRef}
            onMouseMove={handleShowControls}
            onTouchStart={handleShowControls}
            onMouseLeave={handleMouseLeave}
            className={className}
        >
            {isFloating ? <FloatingPlayerOverlay
                playerRef={playerRef}
                onClose={returnFromPIP}
            /> : <>
                {fullScreen && (
                    <Polls outerComponent={playerContainerRef.current} />
                )}
                {!!(isHallsPage && !fullScreen) && (
                    <ButtonClosePlayer onClick={() => setIsShowPlayerHalls(false)} />
                )}
                {!!(!tabletView || fullScreen) && (
                    <Notifications classNameContainer={fullScreen && tabletView ? undefined : "player-notifications"} isPlayer />
                )}
                <Buffering visible={isWaiting && !paused} />
                {!skipRenderControlsDvrDisabled && playerRef.current && paused && <PlayerCenterButtonPlay
                    playerRef={playerRef}
                />}
                {isShowReactions && (
                    <Reactions
                        eventKey={event.eventKey}
                        streamId={stream.id}
                        playerContainerRef={playerContainerRef}
                        isHidden={controlsHidden || !event.moduleMain?.allowedReactions?.length}
                        reactions={event.moduleMain?.allowedReactions || []}
                        onClick={handleShowControls}
                        active={reactionsActive}
                    />
                )}
            </>
            }
            {playerRef.current && !skipRenderControlsDvrDisabled && <Controls
                episode={embedEpisode || episode}
                embedOffset={embedEpisodeOffset}
                onNavigate={handleNavigate}
                controlsHidden={controlsHidden}
                isMobile={isMobile}
                playerRef={playerRef}
                paused={paused}
                currentTime={currentTime}
                streamDay={streamDay}
                duration={duration}
                hasDvr={stream.dvr}
                hasLive={embedEpisode ? isLiveEmbedEpisode : isStreamLive}
                isLive={isLive}
                onLiveButtonClick={() => handleSeek(-1)}
                quality={quality}
                qualities={streamQualities}
                onQualityChange={handleQualityChange}
                language={language}
                languages={streamLanguages}
                onLanguageChange={handleLanguageChange}
                mountMiniPlayer={() => mountPlayer("miniplayer")}
                stream={stream}
                streamEpisodes={streamEpisodes}
                brandingEnabled={event?.moduleBranding?.showComdiBanner}
                fullscreenButtonEnabled={fullscreenSupport(containerRef.current) || fullscreenSupport(playerRef.current)}
                fullScreenSideModuleButtonEnabled={!tabletView && fullscreenSupport(containerRef.current) && (event?.moduleComment?.enabled || event?.moduleQuestion?.enabled)}
                tabletView={tabletView}
                fullScreen={fullScreen}
                onFullScreenClick={toggleFullScreen}
                isShowReactions={isShowReactions}
                onToggleReactions={handleToggleReactions}
                reactionsActive={reactionsActive}
            />}
            <video
                id="stream-player"
                ref={playerRef}
                onClick={handleClick}
                onDoubleClick={() => toggleFullScreen(FULLSCREEN_MODES.VIDEO)}
                onPlay={handlePlay}
                onPause={handlePause}
                onTimeUpdate={(e) => handleTimeUpdate(e)}
                onDurationChange={(e) => setDurationPlayer(e.target.duration)}
                // onSeeked={(e) => handleSeeked(e.target.currentTime, e.target.duration)}
                onVolumeChange={(e) => handleVolumeChange(e.target.volume, e.target.muted)}
                onPlaying={() => setWaiting(false)}
                onCanPlayThrough={handleCanPlayThrough}
                onWaiting={handleWaiting}
                onEnded={handleStop}
                controls={false}
                autoPlay={isStreamLive}
                poster={stream.preview}
                playsInline
            />
        </div>
    );
}
